import { runtime, normalizePath, normalizeSearch, createRouteKey, normalizeRoute } from './Runtime';

const routeMemory = new Map([
    [runtime.route.key, runtime.route]
]);

const requestJson = async (url) => {
    const response = await fetch(url, {
        headers: {
            'Accept': 'application/json',
            'X-WP-Nonce': runtime.system.restNonce || ''
        },
        credentials: 'include',
        cache: 'no-store'
    });
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
        ? await response.json()
        : null;

    return {
        ok: response.ok,
        status: response.status,
        data
    };
};

const hasMissingDynamicItems = (layouts = []) => {
    if(!Array.isArray(layouts)){
        return false;
    }

    return layouts.some((layout) => {
        if(!layout || typeof layout !== 'object'){
            return false;
        }

        const layoutType = layout.acf_fc_layout || layout.layout || layout.type || '';

        if(layoutType === 'cards' && String(layout.source || '') === 'post_type'){
            return !Array.isArray(layout.items);
        }

        if(layoutType !== 'sidebars'){
            return false;
        }

        return hasMissingDynamicItems(layout.sidebar)
            || hasMissingDynamicItems(layout.sections);
    });
};

const routeNeedsDocumentFallback = (route = {}) => {
    return hasMissingDynamicItems(route?.data?.content_flexible);
};

const requestDocumentRoute = async (request) => {
    if(typeof window === 'undefined' || typeof DOMParser === 'undefined'){
        return null;
    }

    const baseUrl = runtime.system.homeUrl || runtime.system.baseUrl || window.location.origin;
    const url = new URL(request.view, baseUrl);
    const response = await fetch(url.toString(), {
        headers: {
            'Accept': 'text/html'
        },
        credentials: 'include',
        cache: 'no-store'
    });

    if(!response.ok){
        return null;
    }

    const html = await response.text();
    const documentNode = new DOMParser().parseFromString(html, 'text/html');
    const bootstrapNode = documentNode.getElementById('reactwp-bootstrap');

    if(!bootstrapNode){
        return null;
    }

    try{
        const bootstrap = JSON.parse(bootstrapNode.textContent || '{}');
        const route = bootstrap?.route;

        if(!route || typeof route !== 'object'){
            return null;
        }

        if(normalizePath(route.path || request.path) !== request.path){
            return null;
        }

        return route;
    } catch(error){
        console.warn('ReactWP document route fallback failed.', error);
        return null;
    }
};

const normalizeRouteRequest = (input) => {
    if(input && typeof input === 'object'){
        const path = normalizePath(input.pathname || input.path || '/');
        const search = normalizeSearch(input.search || '');

        return {
            path,
            search,
            key: createRouteKey(path, search),
            view: `${path}${search}`
        };
    }

    const url = new URL(String(input || '/'), runtime.system.baseUrl || window.location.origin);
    const path = normalizePath(url.pathname || '/');
    const search = normalizeSearch(url.search || '');

    return {
        path,
        search,
        key: createRouteKey(path, search),
        view: `${path}${search}`
    };
};

const normalizeLanguage = (value = '') => {
    const language = String(value || '')
        .trim()
        .toLowerCase()
        .replace('_', '-');

    if(!language){
        return '';
    }

    return language.split('-')[0].replace(/[^a-z0-9]/g, '');
};

const languageFromPath = (path = '') => {
    const segments = normalizePath(path)
        .split('/')
        .filter(Boolean);
    const firstSegment = String(segments[0] || '').toLowerCase();

    return /^[a-z]{2,3}(-[a-z0-9]{2,8})?$/.test(firstSegment)
        ? normalizeLanguage(firstSegment)
        : '';
};

const documentLanguage = () => {
    if(typeof document === 'undefined'){
        return '';
    }

    return normalizeLanguage(document.documentElement?.lang || '');
};

const resolveRouteLanguage = (request) => {
    const currentWindowRoute = typeof window !== 'undefined'
        ? window.ReactWP?.route
        : null;

    return languageFromPath(request.path)
        || normalizeLanguage(currentWindowRoute?.lang)
        || normalizeLanguage(currentWindowRoute?.language)
        || normalizeLanguage(runtime.route?.lang)
        || normalizeLanguage(runtime.route?.language)
        || normalizeLanguage(runtime.site?.language)
        || documentLanguage();
};

const buildRouteUrl = (endpoint, request) => {
    const params = new URLSearchParams({
        view: request.view
    });
    const language = resolveRouteLanguage(request);

    if(language){
        params.set('lang', language);
    }

    return `${endpoint}?${params.toString()}`;
};

export const fetchRoute = async (input) => {
    const request = normalizeRouteRequest(input);

    if(routeMemory.has(request.key)){
        const cachedRoute = routeMemory.get(request.key);

        if(!routeNeedsDocumentFallback(cachedRoute)){
            return cachedRoute;
        }

        routeMemory.delete(request.key);
    }

    const endpoint = runtime.system.routeEndpoint || `${runtime.system.restUrl}reactwp/v1/route`;
    const { data } = await requestJson(buildRouteUrl(endpoint, request));
    let routePayload = data && typeof data === 'object' && data.route
        ? data.route
        : data;

    if(!routePayload || typeof routePayload !== 'object'){
        throw new Error(`ReactWP route payload missing for ${request.view}`);
    }

    if(routeNeedsDocumentFallback(routePayload)){
        const documentRoute = await requestDocumentRoute(request);

        if(documentRoute && !routeNeedsDocumentFallback(documentRoute)){
            routePayload = documentRoute;
        }
    }

    const route = normalizeRoute(routePayload, request.path, request.search);

    if(!routeNeedsDocumentFallback(route)){
        routeMemory.set(route.key, route);
    }

    return route;
};
